'use client';

import { c as _c } from "react/compiler-runtime";
import { Check, ChevronRight } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

/**
 * The modal's whole type scale, so nothing in the flow picks its own size: a
 * screen title, a row's name, the small print beside or beneath either, and a
 * tracked eyebrow. Matches the compact scale the newer modals (Skills, Deploy)
 * settled on.
 */
export const t = {
  title: 'text-sm font-emphasis tracking-heading-3 text-textPrimary',
  label: 'text-xs font-emphasis text-textPrimary',
  meta: 'text-[11px] text-textSecondary',
  micro: 'text-[10px] font-emphasis uppercase tracking-[0.14em] text-textSecondary'
} as const;

/** The one icon tile: 28px, subtle border, brand-tinted glyph. */
export const Tile = t0 => {
  const $ = _c(2);
  const {
    icon: Icon
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-ds-border-standard bg-background-secondary text-primary"><Icon className="size-3.5" /></span>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * The single row every rule control is built from — one plain-English choice
 * with its consequence spelled out underneath. `radio` only changes the
 * indicator shape: pick-one groups look round, pick-any groups look square, so
 * the shape tells you whether choosing this un-chooses something else.
 */
export const ChoiceRow = t0 => {
  const $ = _c(20);
  const {
    icon: Icon,
    label,
    description,
    selected,
    radio,
    onClick
  } = t0;
  const t1 = `flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-colors ${selected ? "border-primary bg-primary/5" : "border-ds-border-standard hover:border-ds-border-hover"}`;
  let t2;
  if ($[0] !== Icon) {
    t2 = Icon && <Icon className="size-4 shrink-0 text-textSecondary" />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <p className={t.label}>{label}</p>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== description) {
    t4 = description && <p className={t.meta}>{description}</p>;
    $[4] = description;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="min-w-0 flex-1">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = `flex size-4 shrink-0 items-center justify-center border-[1.5px] ${radio ? "rounded-full" : "rounded"} ${selected ? "border-primary bg-primary text-white" : "border-ds-border-strong"}`;
  let t7;
  if ($[9] !== selected) {
    t7 = selected && <Check className="size-2.5" strokeWidth={4} />;
    $[9] = selected;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <span className={t6}>{t7}</span>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onClick || $[15] !== t1 || $[16] !== t2 || $[17] !== t5 || $[18] !== t8) {
    t9 = <button type="button" onClick={onClick} className={t1}>{t2}{t5}{t8}</button>;
    $[14] = onClick;
    $[15] = t1;
    $[16] = t2;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
};

/**
 * A row that opens its own screen rather than expanding in place. The value on
 * the right is the whole point: the list has to read as a settings summary you
 * can scan without opening anything.
 */
export const NavRow = t0 => {
  const $ = _c(13);
  const {
    icon: Icon,
    label,
    value,
    attention,
    onClick
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Tile icon={Icon} />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <p className={`shrink-0 ${t.label}`}>{label}</p>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `min-w-0 flex-1 truncate text-end text-[11px] ${attention ? "font-emphasis text-primary" : "text-textSecondary"}`;
  let t4;
  if ($[4] !== t3 || $[5] !== value) {
    t4 = <p className={t3}>{value}</p>;
    $[4] = t3;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronRight className="size-3.5 shrink-0 text-textSecondary" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== onClick || $[9] !== t1 || $[10] !== t2 || $[11] !== t4) {
    t6 = <button type="button" onClick={onClick} className="flex w-full items-center gap-2.5 rounded-lg border border-ds-border-standard px-2.5 py-2 text-left transition-colors hover:border-ds-border-hover hover:bg-primary/5">{t1}{t2}{t4}{t5}</button>;
    $[8] = onClick;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};

/** A plain labelled row for a setting that is only ever on or off. */
export const ToggleRow = t0 => {
  const $ = _c(10);
  const {
    title,
    subtitle,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <p className={t.label}>{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== subtitle) {
    t2 = <p className={t.meta}>{subtitle}</p>;
    $[2] = subtitle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <div className="flex items-center justify-between gap-2.5 rounded-lg border border-ds-border-standard px-2.5 py-2">{t3}{children}</div>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};